import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObject from "../../../api/useFetchObject";
import useFetchObjects from "../../../api/useFetchObjects";
import AcademicFormDialog from "./AcademicFormDialog";
import useUpdate from "../../../api/useUpdate";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { Edit as EditIcon, ArrowBack as BackIcon } from "@mui/icons-material";

const statusLabels = { 1: "Active", 2: "On leave", 3: "Graduated", 4: "Withdrawn" };
const typeLabels = { 1: "Student", 2: "Intern", 3: "Resident" };

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ py: 2 }}>{children}</Box>;
}

export default function AcademicProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const { data: academic, isLoading, isError, error } = useFetchObject(
    ["academic", id],
    "academics",
    id,
    token
  );

  const { data: rotationsData } = useFetchObjects(
    ["rotation-assignments", id],
    `rotation-assignments?academic=${id}`,
    token,
    { enabled: !!id }
  );
  const { data: evaluationsData } = useFetchObjects(
    ["evaluations", id],
    `evaluations?academic=${id}`,
    token,
    { enabled: !!id }
  );
  const { data: skillsData } = useFetchObjects(
    ["skill-assessments", id],
    `skill-assessments?academic=${id}`,
    token,
    { enabled: !!id }
  );
  const { data: mentorsData } = useFetchObjects(
    ["mentor-assignments", id],
    `mentor-assignments?student=${id}`,
    token,
    { enabled: !!id }
  );
  const { data: seminarsData } = useFetchObjects(
    ["seminar-attendances", id],
    `seminar-attendances?academic=${id}`,
    token,
    { enabled: !!id }
  );
  const { data: reportsData } = useFetchObjects(
    ["academic-reports", id],
    `academic-reports?academic=${id}`,
    token,
    { enabled: !!id }
  );

  const { handleUpdate } = useUpdate("academics", token);

  const rotations = Array.isArray(rotationsData) ? rotationsData : (rotationsData?.results || []);
  const evaluations = Array.isArray(evaluationsData) ? evaluationsData : (evaluationsData?.results || []);
  const skills = Array.isArray(skillsData) ? skillsData : (skillsData?.results || []);
  const mentors = Array.isArray(mentorsData) ? mentorsData : (mentorsData?.results || []);
  const seminars = Array.isArray(seminarsData) ? seminarsData : (seminarsData?.results || []);
  const reports = Array.isArray(reportsData) ? reportsData : (reportsData?.results || []);

  const handleSaveEdit = (payload) => {
    handleUpdate(id, payload);
    setEditOpen(false);
  };

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error?.message || "Failed to load academic"}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/academic/profiles")} sx={{ mt: 2 }}>
          {t("common.back", "Back")}
        </Button>
      </Box>
    );
  }

  if (isLoading || !academic) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const name = academic.name_display || `${academic.first_name || ""} ${academic.last_name || ""}`.trim() || "—";

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate("/academic/profiles")} sx={{ mb: 2 }}>
        {t("common.back", "Back")}
      </Button>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {name}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              <Chip size="small" label={academic.academic_type_display || typeLabels[academic.academic_type]} />
              <Chip size="small" color={academic.status === 1 ? "success" : academic.status === 2 ? "warning" : "default"} label={academic.status_display || statusLabels[academic.status]} />
              {academic.trainee_id && <Chip size="small" variant="outlined" label={academic.trainee_id} />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {academic.program && `${t("academic.program", "Program")}: ${academic.program}`}
              {academic.enrollment_date && ` · ${t("academic.enrollmentDate", "Enrollment")}: ${academic.enrollment_date}`}
              {academic.expected_end_date && ` · ${t("academic.expectedEndDate", "Expected end")}: ${academic.expected_end_date}`}
            </Typography>
            {(academic.email || academic.phone) && (
              <Typography variant="body2" color="text.secondary">
                {academic.email} {academic.phone && ` · ${academic.phone}`}
              </Typography>
            )}
          </Box>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
            {t("common.edit", "Edit")}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label={t("academic.rotations", "Rotations")} />
          <Tab label={t("academic.evaluations", "Evaluations")} />
          <Tab label={t("academic.skillAssessments", "Skill assessments")} />
          <Tab label={t("academic.mentorAssignments", "Mentor assignments")} />
          <Tab label={t("academic.seminarAttendances", "Seminar attendances")} />
          <Tab label={t("academic.reports", "Reports")} />
        </Tabs>
        <Box sx={{ px: 2 }}>
          <TabPanel value={tab} index={0}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}><TableCell>{t("academic.rotation", "Rotation")}</TableCell><TableCell>{t("academic.supervisor", "Supervisor")}</TableCell><TableCell>{t("academic.dates", "Dates")}</TableCell></TableRow></TableHead>
                <TableBody>
                  {rotations.length === 0 ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : rotations.map((r) => (<TableRow key={r.id}><TableCell>{r.rotation_display}</TableCell><TableCell>{r.supervisor_display || "—"}</TableCell><TableCell>{r.start_date && r.end_date ? `${r.start_date} – ${r.end_date}` : "—"}</TableCell></TableRow>))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}><TableCell>{t("academic.evaluator", "Evaluator")}</TableCell><TableCell>{t("academic.date", "Date")}</TableCell><TableCell>{t("academic.overallRating", "Rating")}</TableCell></TableRow></TableHead>
                <TableBody>
                  {evaluations.length === 0 ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : evaluations.map((e) => (<TableRow key={e.id}><TableCell>{e.evaluator_display}</TableCell><TableCell>{e.evaluation_date || "—"}</TableCell><TableCell>{e.overall_rating ?? "—"}</TableCell></TableRow>))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}><TableCell>{t("academic.skill", "Skill")}</TableCell><TableCell>{t("academic.level", "Level")}</TableCell><TableCell>{t("academic.date", "Date")}</TableCell></TableRow></TableHead>
                <TableBody>
                  {skills.length === 0 ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : skills.map((s) => (<TableRow key={s.id}><TableCell>{s.skill_name}</TableCell><TableCell>{s.level || "—"}</TableCell><TableCell>{s.assessment_date || "—"}</TableCell></TableRow>))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}><TableCell>{t("academic.mentor", "Mentor")}</TableCell><TableCell>{t("academic.dates", "Dates")}</TableCell></TableRow></TableHead>
                <TableBody>
                  {mentors.length === 0 ? <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : mentors.map((m) => (<TableRow key={m.id}><TableCell>{m.mentor_display}</TableCell><TableCell>{m.start_date && m.end_date ? `${m.start_date} – ${m.end_date}` : "—"}</TableCell></TableRow>))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tab} index={4}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}><TableCell>{t("academic.seminar", "Seminar")}</TableCell><TableCell>{t("academic.date", "Date")}</TableCell><TableCell>{t("academic.attended", "Attended")}</TableCell></TableRow></TableHead>
                <TableBody>
                  {seminars.length === 0 ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : seminars.map((s) => (<TableRow key={s.id}><TableCell>{s.seminar_display}</TableCell><TableCell>{s.seminar?.seminar_date || "—"}</TableCell><TableCell>{s.attended ? t("common.yes", "Yes") : t("common.no", "No")}</TableCell></TableRow>))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tab} index={5}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}><TableCell>{t("academic.reportType", "Type")}</TableCell><TableCell>{t("academic.generatedAt", "Generated")}</TableCell></TableRow></TableHead>
                <TableBody>
                  {reports.length === 0 ? <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : reports.map((r) => (<TableRow key={r.id}><TableCell>{r.report_type}</TableCell><TableCell>{r.generated_at ? new Date(r.generated_at).toLocaleString() : "—"}</TableCell></TableRow>))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Box>
      </Paper>

      <AcademicFormDialog open={editOpen} onClose={() => setEditOpen(false)} academic={academic} onSave={handleSaveEdit} saving={false} />
    </Box>
  );
}
